import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, Clock, Share2, Bookmark, Link2, MapPin, Calendar, Globe, Mail, BadgeCheck, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Pill, GoldButton } from "@/components/Cards";
import BlockRenderer from "@/components/BlockRenderer";
import DOMPurify from "dompurify";
import ContentEngagement from "@/components/ContentEngagement";
import { trackEvent } from "@/lib/firebase";

const ENDPOINT = {
  news: "/news", blog: "/blogs", organizations: "/organizations",
  opportunities: "/opportunities", events: "/events", initiatives: "/initiatives",
  article: "/articles",
};

export default function DetailPage({ type }) {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [item, setItem] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const tracked = useRef("");

  useEffect(() => {
    setItem(null); setNotFound(false);
    api.get(`${ENDPOINT[type]}/${slug}`).then(({ data }) => setItem(data)).catch(() => setNotFound(true));
  }, [type, slug]);
  useEffect(()=>{if(!item?.id||tracked.current===`${type}:${item.id}`)return;tracked.current=`${type}:${item.id}`;const event={blog:"blog_view",article:"article_view",news:"news_view",opportunities:"opportunity_view",events:"event_view",organizations:"organization_view"}[type];if(event)trackEvent(event,{content_slug:item.slug,content_category:item.category,content_type:type})},[item,type]);

  const save = async () => {
    if (!user) { navigate("/login"); return; }
    try {
      const { data } = await api.post("/me/saved", {
        item_id: item.id, item_type: type, title: item.headline || item.title || item.name,
        url: `/${type === "blog" ? "blog" : type}/${item.slug}`,
      });
      toast.success(data.saved ? "Saved to your dashboard" : "Removed from saved");
    } catch { toast.error("Please log in to save."); }
  };
  const share = () => { navigator.clipboard.writeText(window.location.href); toast.success("Link copied to clipboard"); };

  if (notFound) return <Centered><p className="text-dir-muted">Content not found.</p><Link to="/" className="mt-4 text-dir-gold">Back to home</Link></Centered>;
  if (!item) return <Centered><Loader2 className="h-8 w-8 animate-spin text-dir-gold" /></Centered>;

  const isArticle = type === "news" || type === "blog" || type === "article";
  const title = item.headline || item.title || item.name;

  return (
    <div className="min-h-screen bg-dir-bg pt-[72px]" data-testid={`detail-${type}`}>
      <div className="relative h-[42vh] min-h-[320px] w-full overflow-hidden">
        {(item.featured_image || item.image || item.cover) && <img src={item.featured_image || item.image || item.cover} alt={item.featured_image_alt || title} className="h-full w-full object-cover" />}
        <div className="absolute inset-0 bg-gradient-to-t from-dir-bg via-dir-bg/40 to-transparent" />
        <div className="absolute bottom-0 mx-auto w-full max-w-3xl px-4 pb-8 sm:px-6">
          <button onClick={() => navigate(-1)} className="mb-4 inline-flex items-center gap-1 text-sm text-dir-muted hover:text-dir-gold"><ArrowLeft className="h-4 w-4" />Back</button>
          <div className="flex items-center gap-3">{item.category && <Pill>{item.category}</Pill>}{item.verified && <BadgeCheck className="h-5 w-5 text-dir-gold" />}</div>
          <h1 className="mt-4 font-display text-3xl font-bold leading-tight text-dir-text sm:text-4xl">{title}</h1>
        </div>
      </div>

      <motion.article initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mx-auto max-w-3xl px-4 py-12 sm:px-6">
        {isArticle && (
          <div className="mb-8 flex flex-wrap items-center justify-between gap-4 border-b border-dir-gold/15 pb-6">
            <div className="text-sm text-dir-muted">
              By <span className="text-dir-text">{item.author}</span>
              {type === "blog" && (item.published_at || item.created_at) && <span className="ml-3">{new Date(item.published_at || item.created_at).toLocaleDateString()}</span>}
              {item.read_time && <span className="ml-3 inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5" />{item.read_time} min read</span>}
            </div>
            <div className="flex gap-2">
              <IconBtn onClick={save} testId="detail-save"><Bookmark className="h-4 w-4" /></IconBtn>
              <IconBtn onClick={share} testId="detail-share"><Share2 className="h-4 w-4" /></IconBtn>
              <IconBtn onClick={share} testId="detail-copy"><Link2 className="h-4 w-4" /></IconBtn>
            </div>
          </div>
        )}

        {!isArticle && (
          <div className="mb-8 grid gap-3 rounded-2xl border border-dir-gold/15 bg-dir-card p-6 sm:grid-cols-2">
            {item.city && <Meta icon={MapPin} label="Location" value={`${item.city}, ${item.country}`} />}
            {item.country && !item.city && <Meta icon={MapPin} label="Country" value={item.country} />}
            {item.organization && <Meta icon={Globe} label="Organizer" value={item.organization} />}
            {item.date && <Meta icon={Calendar} label="Date" value={item.date} />}
            {item.venue && <Meta icon={MapPin} label="Venue" value={item.venue} />}
            {item.deadline && <Meta icon={Calendar} label="Deadline" value={item.deadline} />}
            {item.status && <Meta icon={BadgeCheck} label="Status" value={item.status} />}
            {item.website && <Meta icon={Globe} label="Website" value={item.website} />}
            {item.email && <Meta icon={Mail} label="Email" value={item.email} />}
          </div>
        )}

        <div className="prose-dir space-y-5 text-[15px] leading-relaxed text-dir-muted">
          {(item.content || item.excerpt || item.description || "").split("\n").filter(Boolean).map((p, i) => (
            <p key={i}>{p}</p>
          ))}
        </div>
        {item.rich_content?.html && <div className="prose-dir mt-8 text-[15px] leading-relaxed text-dir-muted" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(item.rich_content.html) }} />}
        <div className="mt-8"><BlockRenderer blocks={item.blocks} /></div>

        {!isArticle && (
          <div className="mt-10 flex gap-3">
            <GoldButton onClick={() => navigate("/contact")} data-testid="detail-cta">Get in Touch</GoldButton>
          </div>
        )}

        {(item.tags || []).length > 0 && (
          <div className="mt-10 flex flex-wrap gap-2">
            {item.tags.map((t) => <span key={t} className="rounded-full border border-dir-gold/20 px-3 py-1 text-xs text-dir-muted">#{t}</span>)}
          </div>
        )}
        {type === "blog" && <ContentEngagement contentId={item.id} />}
      </motion.article>
    </div>
  );
}

function Centered({ children }) { return <div className="flex min-h-screen flex-col items-center justify-center bg-dir-bg">{children}</div>; }
function IconBtn({ children, onClick, testId }) { return <button data-testid={testId} onClick={onClick} className="flex h-9 w-9 items-center justify-center rounded-full border border-dir-gold/25 text-dir-muted hover:text-dir-gold hover:border-dir-gold/50 transition-colors">{children}</button>; }
function Meta({ icon: Icon, label, value }) {
  return (<div className="flex items-start gap-3"><Icon className="mt-0.5 h-4 w-4 text-dir-gold" /><div><div className="text-[11px] uppercase tracking-wider text-dir-faint">{label}</div><div className="text-sm text-dir-text break-words">{value}</div></div></div>);
}
